/**
 * The frame's render targets at one internal size (05 §8): the scene's colour + normal/outline-class
 * + depth, and the post pass's finished low-resolution image. Made again, and the old ones freed,
 * whenever the pixel size changes (08 §4).
 */

import {
  DepthTexture, HalfFloatType, NearestFilter, RGBAFormat, UnsignedByteType, UnsignedIntType, WebGLRenderTarget,
} from 'three';
import type { RenderSize } from '../size';

export interface FrameTargets {
  /** Two attachments: toon-lit colour (linear), view normal * 0.5 + 0.5 with the outline class in alpha. */
  readonly scene: WebGLRenderTarget;
  readonly depth: DepthTexture;
  /** The post pass's output, sRGB-encoded, sampled by the upscale. */
  readonly post: WebGLRenderTarget;
}

export function createTargets(size: RenderSize): FrameTargets {
  const { targetW, targetH } = size;
  const scene = new WebGLRenderTarget(targetW, targetH, {
    count: 2,
    type: HalfFloatType,
    format: RGBAFormat,
    minFilter: NearestFilter,
    magFilter: NearestFilter,
    depthBuffer: true,
    generateMipmaps: false,
  });
  const depth = new DepthTexture(targetW, targetH);
  depth.type = UnsignedIntType;
  depth.minFilter = NearestFilter;
  depth.magFilter = NearestFilter;
  scene.depthTexture = depth;
  const post = new WebGLRenderTarget(targetW, targetH, {
    type: UnsignedByteType,
    format: RGBAFormat,
    minFilter: NearestFilter,
    magFilter: NearestFilter,
    depthBuffer: false,
    generateMipmaps: false,
  });
  return { scene, depth, post };
}

/** Free the targets' GPU memory (textures, framebuffers, the depth texture). */
export function disposeTargets(t: FrameTargets): void {
  t.scene.dispose();
  t.depth.dispose();
  t.post.dispose();
}
